import { mapValues, sortBy } from 'lodash-es';
import React from 'react';

import { useKey } from '../hooks/useKey';

type ContextActions = {
  addFn: (id: string, priority: number, fn: () => void) => string;
  removeFn: (id: string) => void;
  canClose: () => boolean;
};

type ContextProps = ContextActions;

const initialActions: ContextActions = {
  addFn: () => {
    throw new Error('post message not implemented');
  },
  removeFn: () => {
    throw new Error('post message not implemented');
  },
  canClose: () => false,
};

const EscCancelableContext = React.createContext<ContextProps>(initialActions);

type EscCancelableProviderProps = {
  children: React.ReactNode;
};

type FunctionStack = Record<number, { id: string; fn: () => void }[]>;

export const EscCancelableProvider = ({ children }: EscCancelableProviderProps) => {
  const functionStack = React.useRef<FunctionStack>({});

  useKey(
    'Escape',
    () => {
      const sortedStacks = sortBy(Object.entries(functionStack.current), (d) => Number.parseInt(d[0]))
        .map((s) => s[1])
        .flat(1);

      const lastObj = sortedStacks?.[(sortedStacks.length ?? 0) - 1];
      if (lastObj) {
        lastObj?.fn();
      }
    },
    { dblClickPassthrough: true },
  );

  const addFn = React.useCallback((id: string, priority: number, fn: () => void) => {
    functionStack.current[priority] = [...(functionStack.current[priority] || []), { id, fn }];
    return id;
  }, []);

  const removeFn = React.useCallback((id: string) => {
    functionStack.current = mapValues(functionStack.current, (stack) => stack.filter((c) => c.id != id));
  }, []);

  const canClose = React.useCallback(
    () => Object.values(functionStack.current).some((stack) => stack.length > 0),
    [],
  );

  const value: ContextProps = {
    addFn,
    removeFn,
    canClose,
  };

  return <EscCancelableContext.Provider value={value}>{children}</EscCancelableContext.Provider>;
};

export const useEscCancelableContext = () => React.useContext(EscCancelableContext);

export const useEscStack = (id: string, priority: number, fn?: () => void) => {
  const { addFn, removeFn } = useEscCancelableContext();

  React.useEffect(() => {
    if (fn) {
      addFn(id, priority, fn);
      return () => {
        removeFn(id);
      };
    }
    return undefined;
  }, [addFn, removeFn, fn, id, priority]);
};

export const ESC_PRIORITY = {
  LOW: 0,
  MED: 100,
  ACTION_BAR: 400,
  INTERSTITIAL: 5000,
};
